/**
 * Weekday-mask helpers shared between client and server.
 * Bit 0 = Monday, bit 1 = Tuesday, ..., bit 6 = Sunday.
 *
 * Why bit 0 = Monday? European convention; Serbian/Bosnian/Croatian calendars
 * also start the week on Monday. ISO 8601 agrees (Mon=1, Sun=7).
 */

/** All 7 weekdays in display order. */
export const WEEKDAYS_MON_FIRST = [0, 1, 2, 3, 4, 5, 6] as const;
export type WeekdayIndex = (typeof WEEKDAYS_MON_FIRST)[number];

export function maskToSet(mask: number): Set<WeekdayIndex> {
  const s = new Set<WeekdayIndex>();
  for (const i of WEEKDAYS_MON_FIRST) {
    if (mask & (1 << i)) s.add(i);
  }
  return s;
}

export function setToMask(set: Iterable<WeekdayIndex>): number {
  let m = 0;
  for (const i of set) m |= 1 << i;
  return m;
}

export function maskHas(mask: number, day: WeekdayIndex): boolean {
  return (mask & (1 << day)) !== 0;
}

export function toggleDay(mask: number, day: WeekdayIndex): number {
  return mask ^ (1 << day);
}

/**
 * Convert a JS Date to our Mon-first weekday index.
 * JS Date.getDay() returns 0..6 with Sunday=0; we shift to Monday=0.
 */
export function jsDayToIndex(jsDay: number): WeekdayIndex {
  return (((jsDay + 6) % 7) as WeekdayIndex);
}

/** "Mon", "Tue" etc. labels keyed by short code so callers can localize. */
export const WEEKDAY_SHORT_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

/** All-7 (1+2+4+8+16+32+64) = 127. */
export const WEEKDAY_MASK_ALL = 127;
/** Mon-Fri only. */
export const WEEKDAY_MASK_WEEKDAYS = 0b0011111; // 31
/** Sat-Sun only. */
export const WEEKDAY_MASK_WEEKENDS = 0b1100000; // 96
